/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Purpose } from '../managers/sso';
import { normalizeDialingCode } from '../utils/phoneUtils';
import { useCultureCode } from './useCultureCode';
import { useUser } from './account';
import { useMutationErrorState } from './useMutationErrorState';
import {
  useRequestEmailOtp,
  useRequestPhoneNumberOtp,
  useVerifyEmailOtp,
  useVerifyOtpAndUpdatePhoneNumber,
} from './otp/api';

import { describeError } from '../utils/describeError';
export interface UsePhoneNumberUpdateFlowOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

export function usePhoneNumberUpdateFlow(
  options?: UsePhoneNumberUpdateFlowOptions,
) {
  const { t } = useTranslation('account');
  const { data: user } = useUser('usePhoneNumberUpdateFlow');
  const cultureCode = useCultureCode('usePhoneNumberUpdateFlow');

  const optionsRef = useRef(options);
  optionsRef.current = options;

  const requestEmailOtpMutation = useRequestEmailOtp(
    'usePhoneNumberUpdateFlow',
  );
  const verifyEmailOtpMutation = useVerifyEmailOtp('usePhoneNumberUpdateFlow');
  const requestPhoneNumberOtpMutation = useRequestPhoneNumberOtp(
    'usePhoneNumberUpdateFlow',
  );
  const verifyOtpAndUpdatePhoneNumberMutation =
    useVerifyOtpAndUpdatePhoneNumber('usePhoneNumberUpdateFlow');

  const { errorMessage, errorCode, clearError, fail, run, runAsync } =
    useMutationErrorState(options?.onError);

  const requestEmailOtp = useCallback(
    (onSuccess?: () => void, onError?: () => void) => {
      if (!user?.username) {
        const error = 'Missing user email';
        console.error(describeError(error));
        fail(error);
        onError?.();
        return;
      }

      run(
        requestEmailOtpMutation,
        {
          email: user.username,
          cultureCode: cultureCode,
          purpose: Purpose.PHONE_CHANGE,
        },
        {
          fallbackMessage: t('verifyEmailPage.requestFailed'),
          onSuccess: () => onSuccess?.(),
          onError: () => onError?.(),
        },
      );
    },
    [user, requestEmailOtpMutation, cultureCode, t, fail, run],
  );

  const verifyEmailOtp = useCallback(
    async (otp: string, onSuccess?: () => void | Promise<void>) => {
      if (!user?.username) {
        const error = 'Missing user email';
        console.error(describeError(error));
        fail(error);
        return;
      }

      await runAsync(
        verifyEmailOtpMutation,
        { email: user.username, otp, purpose: Purpose.PHONE_CHANGE },
        {
          fallbackMessage: t('verifyEmailPage.verificationFailed'),
          onSuccess: () => onSuccess?.(),
        },
      );
    },
    [user, verifyEmailOtpMutation, t, fail, runAsync],
  );

  const requestNewPhoneOtp = useCallback(
    (
      dialingCode: string,
      phoneNumber: string,
      onSuccess?: () => void,
      onError?: () => void,
    ) => {
      if (!dialingCode || !phoneNumber) {
        const error = 'Missing dialing code or phone number';
        console.error(describeError(error));
        fail(error);
        onError?.();
        return;
      }

      run(
        requestPhoneNumberOtpMutation,
        {
          requestOtpBody: {
            dialingCode: normalizeDialingCode(dialingCode),
            phoneNumber: phoneNumber,
            ...(user?.cultureCode && { cultureCode: user.cultureCode }),
          },
          otpPurpose: Purpose.PHONE_CHANGE,
        },
        {
          fallbackMessage: t('verifyPhonePage.requestFailed'),
          onSuccess: () => onSuccess?.(),
          onError: () => onError?.(),
        },
      );
    },
    [user, requestPhoneNumberOtpMutation, t, fail, run],
  );

  const verifyNewPhoneOtp = useCallback(
    (otp: string, onSuccess?: () => void) => {
      run(
        verifyOtpAndUpdatePhoneNumberMutation,
        { otp: otp },
        {
          fallbackMessage: t('verifyPhonePage.verificationFailed'),
          onSuccess: () => {
            onSuccess?.();
            optionsRef.current?.onSuccess?.();
          },
        },
      );
    },
    [verifyOtpAndUpdatePhoneNumberMutation, t, run],
  );

  const isLoading =
    requestEmailOtpMutation.isPending ||
    verifyEmailOtpMutation.isPending ||
    requestPhoneNumberOtpMutation.isPending ||
    verifyOtpAndUpdatePhoneNumberMutation.isPending;

  return useMemo(
    () => ({
      errorMessage,
      errorCode,
      isLoading,

      requestEmailOtp,
      verifyEmailOtp,
      requestNewPhoneOtp,
      verifyNewPhoneOtp,
      clearError,

      isRequestingEmailOtp: requestEmailOtpMutation.isPending,
      isVerifyingEmailOtp: verifyEmailOtpMutation.isPending,
      isRequestingNewPhoneOtp: requestPhoneNumberOtpMutation.isPending,
      isVerifyingAndUpdatingPhoneNumber:
        verifyOtpAndUpdatePhoneNumberMutation.isPending,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      errorMessage,
      errorCode,
      isLoading,
      requestEmailOtpMutation.isPending,
      verifyEmailOtpMutation.isPending,
      requestPhoneNumberOtpMutation.isPending,
      verifyOtpAndUpdatePhoneNumberMutation.isPending,
    ],
  );
}
